import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { FaPlay, FaTimes, FaImage, FaChevronLeft, FaChevronRight, FaVideo, FaFolder, FaTh, FaList, FaSort } from 'react-icons/fa';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import API_BASE_URL from '../config';
import SEO from './SEO';
import './Gallery.css';

const Gallery = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [albums, setAlbums] = useState([]);
  const [videos, setVideos] = useState([]);
  const [legacyGalleryItems, setLegacyGalleryItems] = useState([]); // Старые фото из galleryItems

  // Функция для обрезки названия альбома до 25 символов
  const truncateAlbumName = (name, maxLength = 25) => {
    if (!name) return '';
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  // Функция для проверки, является ли описание автоматическим (нужно скрыть)
  const isAutoDescription = (description) => {
    if (!description) return false;
    const autoKeywords = ['Альбом создан автоматически', 'Создан автоматически', 'created automatically', 'Album created automatically'];
    return autoKeywords.some(keyword => description.includes(keyword));
  };
  
  // Восстанавливаем состояние из localStorage при инициализации
  const [selectedAlbum, setSelectedAlbum] = useState(() => {
    const saved = localStorage.getItem('gallery_selectedAlbum');
    return saved || null;
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('gallery_activeTab');
    return saved || 'albums';
  }); // 'albums' or 'videos'
  const [albumSortBy, setAlbumSortBy] = useState('date'); // 'date', 'name', 'photos'
  const [currentVideoAlbum, setCurrentVideoAlbum] = useState(null); // Текущий открытый видеоальбом с полными данными

  // Принудительно устанавливаем grid режим на мобильных устройствах
  useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth <= 768) {
        if (viewMode !== 'grid') {
          setViewMode('grid');
        }
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [viewMode]);

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/albums`, {
        params: { sort: albumSortBy }
      });
      // Убеждаемся, что videos массив инициализирован для всех альбомов
      const albumsWithVideos = response.data.map(album => ({
        ...album,
        videos: Array.isArray(album.videos) ? album.videos : []
      }));
      
      setAlbums(albumsWithVideos);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching albums:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchAlbums(),
        fetchLegacyGallery() // Загружаем старые фото
      ]);
      // Загружаем видео только если активна вкладка Videos
      if (activeTab === 'videos') {
        await fetchVideos();
      }
    };
    loadData();
    
    // Если был сохранен выбранный альбом, восстанавливаем его после загрузки данных
    const savedAlbum = localStorage.getItem('gallery_selectedAlbum');
    if (savedAlbum && !selectedAlbum) {
      // Небольшая задержка, чтобы данные успели загрузиться
      setTimeout(() => {
        setSelectedAlbum(savedAlbum);
      }, 100);
    }
    
    // Обновляем при возврате на вкладку браузера
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchLegacyGallery();
        fetchAlbums();
        if (activeTab === 'videos') {
          fetchVideos();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumSortBy, activeTab]);

  // Сохраняем состояние в localStorage при изменении
  useEffect(() => {
    localStorage.setItem('gallery_activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (selectedAlbum) {
      localStorage.setItem('gallery_selectedAlbum', selectedAlbum);
    } else {
      localStorage.removeItem('gallery_selectedAlbum');
    }
  }, [selectedAlbum]);

  // Сбрасываем состояние при переходе на страницу Gallery через навигацию
  useEffect(() => {
    if (location.pathname === '/gallery') {
      const savedAlbum = localStorage.getItem('gallery_selectedAlbum');
      // Если localStorage был очищен (при клике на Gallery в навигации), сбрасываем состояние
      if (!savedAlbum && selectedAlbum) {
        setSelectedAlbum(null);
        setSelectedItem(null);
        setSelectedIndex(0);
      }
    }
  }, [location.pathname]);

  // Обновляем альбомы при переключении на вкладку Videos
  useEffect(() => {
    if (activeTab === 'videos') {
      fetchAlbums();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Загружаем полные данные альбома при его открытии (для видеоальбомов)
  useEffect(() => {
    if (selectedAlbum && activeTab === 'videos') {
      const loadAlbumData = async () => {
        try {
          // Загружаем все альбомы, чтобы получить актуальные данные
          const response = await axios.get(`${API_BASE_URL}/api/albums`);
          const allAlbumsData = response.data || [];
          
          // Ищем альбом в загруженных данных
          let album = allAlbumsData.find(a => a.id === selectedAlbum);
          
          // Если не нашли в обычных альбомах, это может быть виртуальный альбом
          if (!album && selectedAlbum === 'legacy-video-album') {
            setCurrentVideoAlbum(null);
            return;
          }
          
          if (album) {
            // Убеждаемся, что videos массив инициализирован
            const albumWithVideos = {
              ...album,
              videos: Array.isArray(album.videos) ? album.videos : []
            };
            
            setCurrentVideoAlbum(albumWithVideos);
          } else {
            setCurrentVideoAlbum(null);
          }
        } catch (error) {
          console.error('❌ Error loading album data:', error);
          setCurrentVideoAlbum(null);
        }
      };
      
      loadAlbumData();
    } else {
      setCurrentVideoAlbum(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlbum, activeTab]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/videos`);
      const newVideos = response.data || [];
      
      // Также загружаем старые видео из gallery для обратной совместимости
      try {
        const legacyResponse = await axios.get(`${API_BASE_URL}/api/gallery`);
        if (Array.isArray(legacyResponse.data)) {
          const legacyVideos = legacyResponse.data.filter(item => item.type === 'video');
          if (legacyVideos.length > 0) {
            // Проверяем, какие видео уже есть в новом списке
            const existingVideoUrls = new Set(newVideos.map(v => v.url));
            const additionalVideos = legacyVideos
              .filter(v => !existingVideoUrls.has(v.url))
              .map(v => ({
                id: v.id,
                url: v.url,
                title: v.title,
                description: v.description || '',
                thumbnail: null,
                uploadedAt: v.createdAt || new Date().toISOString()
              }));
            setVideos([...additionalVideos, ...newVideos]);
            return;
          }
        }
      } catch (legacyError) {
        console.error('Error fetching legacy videos:', legacyError);
      }
      
      setVideos(newVideos);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const fetchLegacyGallery = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/gallery`);
      // Фильтруем только изображения (видео обрабатываются в fetchVideos)
      const images = Array.isArray(response.data) 
        ? response.data.filter(item => item.type === 'image')
        : [];
      setLegacyGalleryItems(images);
    } catch (error) {
      console.error('Error fetching legacy gallery:', error);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  // Создаем виртуальный альбом для старых фото
  const legacyAlbum = useMemo(() => 
    legacyGalleryItems.length > 0 ? {
      id: 'legacy-album',
      name: t('gallery.allPhotos') || 'All Photos',
      photos: legacyGalleryItems.map(item => ({
        id: item.id,
        url: item.url,
        title: item.title,
        description: item.description,
        uploadedAt: item.createdAt || new Date().toISOString()
      })),
      coverPhoto: legacyGalleryItems[0]?.url || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    } : null,
    [legacyGalleryItems, t]
  );

  // Фильтруем альбомы: для фото показываем только альбомы с фото, для видео - только альбомы с видео
  const photoAlbums = useMemo(() => albums.filter(album => album.photos && album.photos.length > 0), [albums]);
  const videoAlbums = useMemo(() => {
    return albums.filter(album => {
      return Array.isArray(album.videos) && album.videos.length > 0;
    });
  }, [albums]);

  // Создаем виртуальный альбом для всех отдельных видео (не в альбомах)
  const legacyVideoAlbum = useMemo(() => {
    // Получаем все видео, которые не находятся в альбомах
    const videosNotInAlbums = videos.filter(video => {
      return !albums.some(album => 
        album.videos && album.videos.some(albumVideo => albumVideo.id === video.id)
      );
    });
    
    return videosNotInAlbums.length > 0 ? {
      id: 'legacy-video-album',
      name: t('gallery.allVideos') || 'All Videos',
      videos: videosNotInAlbums.map(video => ({
        id: video.id,
        url: video.url,
        title: video.title,
        description: video.description || '',
        uploadedAt: video.uploadedAt || new Date().toISOString()
      })),
      // Используем последнее видео как обложку
      coverPhoto: videosNotInAlbums[videosNotInAlbums.length - 1]?.url || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    } : null;
  }, [videos, albums, t]);

  // Объединяем альбомы с виртуальным альбомом старых фото (только для фото)
  const allAlbums = useMemo(() => 
    legacyAlbum 
      ? [legacyAlbum, ...photoAlbums]
      : photoAlbums,
    [legacyAlbum, photoAlbums]
  );
  
  // Альбомы с видео - объединяем с виртуальным альбомом
  const allVideoAlbums = useMemo(() => 
    legacyVideoAlbum 
      ? [legacyVideoAlbum, ...videoAlbums]
      : videoAlbums,
    [legacyVideoAlbum, videoAlbums]
  );

  // Получаем все фото из выбранного альбома для навигации
  const currentAlbumPhotos = useMemo(() => 
    selectedAlbum 
      ? (allAlbums.find(a => a.id === selectedAlbum)?.photos || [])
      : [],
    [selectedAlbum, allAlbums]
  );

  const handleAlbumClick = (album) => {
    setSelectedAlbum(album.id);
    setSelectedItem(null);
    localStorage.setItem('gallery_selectedAlbum', album.id);
  };

  const handleVideoAlbumClick = (album) => {
    setSelectedAlbum(album.id);
    setSelectedItem(null);
    localStorage.setItem('gallery_selectedAlbum', album.id);
  };

  // Обновляем список при изменении альбомов (убираем зависимость от selectedAlbum, чтобы избежать циклов)

  const handlePhotoClick = (photo, albumId) => {
    const album = allAlbums.find(a => a.id === albumId);
    if (!album) return;
    
    const index = album.photos.findIndex(p => p.id === photo.id);
      setSelectedIndex(index >= 0 ? index : 0);
    setSelectedItem({ ...photo, albumId, type: 'image' });
  };

  const handleVideoClick = (video) => {
    setSelectedItem({ ...video, type: 'video' });
  };

  // Обработчики для предпросмотра видео при наведении
  const handleVideoMouseEnter = (e) => {
    const video = e.target;
    if (video.tagName === 'VIDEO') {
      video.currentTime = 0;
      video.play().catch(err => {
        // Игнорируем ошибки автозапуска
        console.log('Video autoplay prevented:', err);
      });
    }
  };

  const handleVideoMouseLeave = (e) => {
    const video = e.target;
    if (video.tagName === 'VIDEO') {
      video.pause();
      video.currentTime = 0;
    }
  };

  const handlePrevious = useCallback((e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (currentAlbumPhotos.length === 0) return;
    setSelectedIndex(prevIndex => {
      const newIndex = prevIndex === 0 ? currentAlbumPhotos.length - 1 : prevIndex - 1;
      const photo = currentAlbumPhotos[newIndex];
      setSelectedItem({ ...photo, albumId: selectedAlbum, type: 'image' });
      return newIndex;
    });
  }, [currentAlbumPhotos, selectedAlbum]);

  const handleNext = useCallback((e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (currentAlbumPhotos.length === 0) return;
    setSelectedIndex(prevIndex => {
      const newIndex = prevIndex === currentAlbumPhotos.length - 1 ? 0 : prevIndex + 1;
      const photo = currentAlbumPhotos[newIndex];
      setSelectedItem({ ...photo, albumId: selectedAlbum, type: 'image' });
      return newIndex;
    });
  }, [currentAlbumPhotos, selectedAlbum]);

  const handleClose = useCallback(() => {
    setSelectedItem(null);
    if (currentAlbumPhotos.length > 0) {
      setSelectedIndex(0);
    }
  }, [currentAlbumPhotos.length]);

  // Синхронизация selectedIndex с selectedItem
  useEffect(() => {
    if (selectedItem && selectedItem.albumId) {
      const album = allAlbums.find(a => a.id === selectedItem.albumId);
      if (album) {
        const index = album.photos.findIndex(p => p.id === selectedItem.id);
      if (index >= 0 && index !== selectedIndex) {
        setSelectedIndex(index);
      }
    }
    }
  }, [selectedItem, allAlbums, selectedIndex]);

  // Обработка клавиатуры
  useEffect(() => {
    if (!selectedItem) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItem, handlePrevious, handleNext, handleClose]);

  if (loading) {
    return (
      <div className="gallery-container">
        <div className="loading">{t('gallery.loading') || 'Loading...'}</div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Gallery"
        description="Photo and video gallery of Sergey Levitskiy - DJ performances, events, and music production. Browse albums and videos from commercial and underground projects."
        keywords="Sergey Levitskiy gallery, DJ photos, music videos, event photos, performance photos"
      />
      <div className="gallery-container">
      {/* Креативные эффекты фона для Gallery */}
      <div className="gallery-mesh-overlay"></div>
      <div className="gallery-light-rays"></div>
      
      <motion.div
        className="gallery-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Заголовок и переключатели */}
        <div className="gallery-header">
          {!selectedAlbum && (
            <div className="gallery-controls">
              <div className="view-mode-toggle">
                <button
                  className={viewMode === 'grid' ? 'active' : ''}
                  onClick={() => setViewMode('grid')}
                  title={t('gallery.gridView') || 'Grid view'}
                >
                  <FaTh />
                </button>
                <button
                  className={viewMode === 'list' ? 'active' : ''}
                  onClick={() => setViewMode('list')}
                  title={t('gallery.listView') || 'List view'}
                >
                  <FaList />
                </button>
              </div>
              <div className="sort-controls">
                <FaSort style={{ marginRight: '0.5rem' }} />
                <select 
                  value={albumSortBy} 
                  onChange={(e) => {
                    setAlbumSortBy(e.target.value);
                    fetchAlbums();
                  }}
                  className="sort-select"
                >
                  <option value="date">{t('gallery.sortByDate') || 'By date'}</option>
                  <option value="name">{t('gallery.sortByName') || 'By name'}</option>
                  <option value="photos">{t('gallery.sortByPhotos') || 'By count'}</option>
                </select>
              </div>
              <div className="tab-toggle">
                <button
                  className={activeTab === 'albums' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('albums');
                    setSelectedAlbum(null);
                    setSelectedItem(null);
                    localStorage.setItem('gallery_activeTab', 'albums');
                    localStorage.removeItem('gallery_selectedAlbum');
                  }}
                >
                  <FaFolder /> {t('gallery.albums') || 'Albums'}
                </button>
                <button
                  className={activeTab === 'videos' ? 'active' : ''}
                  onClick={async () => {
                    setActiveTab('videos');
                    setSelectedAlbum(null);
                    setSelectedItem(null);
                    localStorage.setItem('gallery_activeTab', 'videos');
                    localStorage.removeItem('gallery_selectedAlbum');
                    // Загружаем видео при переключении на вкладку Videos
                    await fetchVideos();
                  }}
                >
                  <FaVideo /> {t('gallery.videos') || 'Videos'}
                </button>
              </div>
            </div>
          )}

          </div>

        {/* Контент */}
        {selectedAlbum ? (
          // Просмотр альбома
          activeTab === 'albums' ? (() => {
            const album = allAlbums.find(a => a.id === selectedAlbum);
            if (!album) return null;
            
            return (
          <motion.div
            className="gallery-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
                <div 
                  className="album-header"
                  onClick={() => {
                    setSelectedAlbum(null);
                    setSelectedItem(null);
                    localStorage.removeItem('gallery_selectedAlbum');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <h2>{truncateAlbumName(album.name)}</h2>
                    {album.description && !isAutoDescription(album.description) && <p>{album.description}</p>}
                    <p className="album-count">{album.photos.length} {album.photos.length === 1 ? (t('gallery.photo') || 'photo') : (t('gallery.photos') || 'photos')}</p>
                  </div>
                  <div className="album-header-footer">
                    <button
                      className="back-button-inline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAlbum(null);
                        setSelectedItem(null);
                        localStorage.removeItem('gallery_selectedAlbum');
                      }}
                    >
                      <span className="back-arrow">&lt;</span> {t('gallery.back') || 'Back'}
                    </button>
                  </div>
                </div>
                
                {album.photos.length === 0 ? (
                  <div className="empty-gallery">
                    <FaImage className="empty-icon" />
                    <p>{t('gallery.noPhotosInAlbum') || 'No photos in this album yet.'}</p>
                  </div>
                ) : (
                  album.photos.map((photo) => (
              <motion.div
                      key={photo.id}
                className="gallery-item"
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -10 }}
                      onClick={() => handlePhotoClick(photo, selectedAlbum)}
              >
                  <div className="gallery-image">
                    <img 
                          src={`${API_BASE_URL}${photo.url}`} 
                          alt={photo.title}
                          loading="lazy"
                    />
                    <div className="gallery-overlay">
                      <FaImage />
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </motion.div>
            );
          })() : (
            // Просмотр видеоальбома
            (() => {
              // ПРИОРИТЕТ: Используем currentVideoAlbum, если он загружен
              // Это гарантирует, что мы используем свежие данные с сервера
              let album = null;
              
              if (currentVideoAlbum && currentVideoAlbum.id === selectedAlbum) {
                album = currentVideoAlbum;
              } else {
                // Сначала ищем в albums (основной источник данных с сервера)
                album = albums.find(a => a.id === selectedAlbum);
                
                // Если не нашли в albums, проверяем allVideoAlbums (может быть виртуальный альбом)
              if (!album) {
                  album = allVideoAlbums.find(a => a.id === selectedAlbum);
                }
              }
              
              if (!album) {
                return (
                  <div className="empty-gallery">
                    <FaVideo className="empty-icon" />
                    <p>{t('gallery.albumNotFound') || 'Album not found.'}</p>
                  </div>
                );
              }
              
              // Убеждаемся, что videos массив существует и является массивом
              const albumVideos = Array.isArray(album.videos) ? album.videos : [];
              
              return (
                <>
                  <motion.div
                    key={`album-${selectedAlbum}-${currentVideoAlbum ? currentVideoAlbum.videos?.length || 0 : 0}`}
                    className="gallery-grid"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <div 
                      className="album-header"
                      onClick={() => {
                        setSelectedAlbum(null);
                        setSelectedItem(null);
                        localStorage.removeItem('gallery_selectedAlbum');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div>
                        <h2>{truncateAlbumName(album.name)}</h2>
                        {album.description && !isAutoDescription(album.description) && <p>{album.description}</p>}
                        <p className="album-count">{albumVideos.length} {albumVideos.length === 1 ? (t('gallery.video') || 'video') : (t('gallery.videos') || 'videos')}</p>
                      </div>
                      <div className="album-header-footer">
                        <button
                          className="back-button-inline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAlbum(null);
                            setSelectedItem(null);
                            localStorage.removeItem('gallery_selectedAlbum');
                          }}
                        >
                          <span className="back-arrow">&lt;</span> {t('gallery.back') || 'Back'}
                        </button>
                      </div>
                    </div>
                  
                  {albumVideos.length === 0 ? (
                    <div className="empty-gallery">
                      <FaVideo className="empty-icon" />
                      <p>{t('gallery.noVideosInAlbum') || 'No videos in this album yet.'}</p>
                  </div>
                ) : (
                    albumVideos.map((video) => (
                      <motion.div
                        key={video.id}
                        className="gallery-item"
                        variants={itemVariants}
                        whileHover={{ scale: 1.05, y: -10 }}
                        onClick={() => handleVideoClick(video)}
                      >
                        <div className="gallery-image">
                          <video 
                            src={`${API_BASE_URL}${video.url}`}
                            muted
                            preload="metadata"
                            onMouseEnter={handleVideoMouseEnter}
                            onMouseLeave={handleVideoMouseLeave}
                            onLoadedMetadata={(e) => {
                              e.target.currentTime = 0;
                            }}
                          />
                    <div className="gallery-overlay">
                      <FaPlay />
                    </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </motion.div>
                </>
              );
            })()
          )
        ) : activeTab === 'albums' ? (
          // Список альбомов
          allAlbums.length === 0 ? (
              <div className="empty-gallery">
              <FaFolder className="empty-icon" />
              <p>{t('gallery.noAlbums') || 'No albums yet.'}</p>
              </div>
            ) : (
                  <motion.div
                    key={`albums-${viewMode}`}
                    className={`albums-grid ${viewMode === 'list' ? 'list-view' : ''}`}
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    layout
                    transition={{
                      layout: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
                      staggerChildren: 0.05,
                      delayChildren: 0.1
                    }}
                  >
              {allAlbums.map((album) => (
                        <motion.div
                          key={album.id}
                          className={`album-card ${viewMode === 'list' ? 'list-item' : ''}`}
                          variants={itemVariants}
                          whileHover={{ scale: 1.05, y: -10 }}
                          layout
                          transition={{
                            layout: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
                            type: "spring",
                            stiffness: 100
                          }}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                  onClick={() => handleAlbumClick(album)}
                        >
                          <div className="album-cover">
                    {album.coverPhoto ? (
                              <img 
                                src={`${API_BASE_URL}${album.coverPhoto}`} 
                                alt={album.name}
                                loading="lazy"
                              />
                            ) : (
                              <div className="album-placeholder">
                        <FaFolder />
                              </div>
                            )}
                            <div className="album-overlay">
                      <FaFolder />
                      <span>{album.photos.length}</span>
                            </div>
                          </div>
                          <AnimatePresence mode="wait">
                            {viewMode === 'list' && (
                              <motion.div
                                className="album-info"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                                key="list-info"
                              >
                                <h3>{truncateAlbumName(album.name)}</h3>
                                {album.description && !isAutoDescription(album.description) && <p>{album.description}</p>}
                        <p className="album-count">{album.photos.length} {album.photos.length === 1 ? (t('gallery.photo') || 'photo') : (t('gallery.photos') || 'photos')}</p>
                              </motion.div>
                            )}
                            {viewMode === 'grid' && (
                              <motion.div
                                className="album-info-compact"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                                key="grid-info"
                              >
                                <h3>{truncateAlbumName(album.name)}</h3>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
              ))}
                  </motion.div>
          )
        ) : (
          // Вкладка Videos: список альбомов с видео
          allVideoAlbums.length === 0 ? (
            <div className="empty-gallery">
              <FaVideo className="empty-icon" />
              <p>{t('gallery.noVideos') || 'No videos yet.'}</p>
            </div>
          ) : (
                <motion.div
                  key={`videos-${viewMode}`}
              className={`albums-grid ${viewMode === 'list' ? 'list-view' : ''}`}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  layout
                  transition={{
                    layout: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
                    staggerChildren: 0.05,
                    delayChildren: 0.1
                  }}
                >
              {allVideoAlbums.map((album) => {
                // Используем последнее видео как обложку
                const lastVideo = album.videos && album.videos.length > 0 
                  ? album.videos[album.videos.length - 1] 
                  : null;
                // Для виртуального альбома coverPhoto может быть URL видео, поэтому проверяем тип
                const hasCoverPhoto = album.coverPhoto && album.id !== 'legacy-video-album';
                
                return (
                    <motion.div
                    key={album.id}
                    className={`album-card ${viewMode === 'list' ? 'list-item' : ''}`}
                      variants={itemVariants}
                      whileHover={{ scale: 1.05, y: -10 }}
                      layout
                      transition={{
                        layout: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
                        type: "spring",
                        stiffness: 100
                      }}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                    onClick={() => handleVideoAlbumClick(album)}
                    >
                    <div className="album-cover">
                      {hasCoverPhoto ? (
                        <img 
                          src={`${API_BASE_URL}${album.coverPhoto}`} 
                          alt={album.name}
                          loading="lazy"
                        />
                      ) : lastVideo ? (
                        <video 
                          src={`${API_BASE_URL}${lastVideo.url}`}
                          muted
                          preload="metadata"
                          onMouseEnter={handleVideoMouseEnter}
                          onMouseLeave={handleVideoMouseLeave}
                          onLoadedMetadata={(e) => {
                            e.target.currentTime = 0;
                          }}
                        />
                      ) : (
                        <div className="album-placeholder">
                          <FaVideo />
                        </div>
                      )}
                      <div className="album-overlay">
                        <FaVideo />
                        <span>{album.videos.length}</span>
                        </div>
                      </div>
                      <AnimatePresence mode="wait">
                        {viewMode === 'list' && (
                          <motion.div
                            className="album-info"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                            key="list-info"
                          >
                            <h3>{truncateAlbumName(album.name)}</h3>
                            {album.description && !isAutoDescription(album.description) && <p>{album.description}</p>}
                            <p className="album-count">{album.videos.length} {album.videos.length === 1 ? (t('gallery.video') || 'video') : (t('gallery.videos') || 'videos')}</p>
                          </motion.div>
                        )}
                        {viewMode === 'grid' && (
                          <motion.div
                            className="album-info-compact"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                            key="grid-info"
                          >
                            <h3>{truncateAlbumName(album.name)}</h3>
                          </motion.div>
                        )}
                      </AnimatePresence>
              </motion.div>
                );
              })}
          </motion.div>
          )
        )}
      </motion.div>

      {/* Модальное окно для просмотра */}
      <AnimatePresence mode="wait">
        {selectedItem && (
          <motion.div
            className="gallery-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              key={selectedItem.id}
            >
              <button
                className="close-button"
                onClick={handleClose}
                aria-label={t('gallery.close') || 'Close'}
              >
                <FaTimes />
              </button>

              {/* Кнопки навигации только для фото */}
              {selectedItem.type !== 'video' && currentAlbumPhotos.length > 1 && (
                <>
                  <button
                    className="nav-button nav-button-prev"
                    onClick={handlePrevious}
                    aria-label={t('gallery.previousImage') || 'Previous image'}
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    className="nav-button nav-button-next"
                    onClick={handleNext}
                    aria-label={t('gallery.nextImage') || 'Next image'}
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}

              {selectedItem.type === 'video' ? (
                <video src={`${API_BASE_URL}${selectedItem.url}`} controls autoPlay />
              ) : (
                <motion.img 
                  src={`${API_BASE_URL}${selectedItem.url}`} 
                  alt={selectedItem.title}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default Gallery;
